/**
 * All exports in this file follow the same pattern:
 * 1. Get the data for the tabs
 * 2. Create a blank workbook
 * 3. Add one or more worksheets for the relevant data
 * 4. Save the file
 * 
 * There are helper functions will add the sheet to a workbook provided you pass in
 * - the workbook
 * - the sheet name
 * - the headers to render out
 * - the rows to add
 * 
 */

import moment from 'moment';
import ExcelJS from "exceljs";
import FileSaver from "file-saver";
import * as specimenAPI from "./specimenAPI";
import * as venomAnalysisAPI from './venomAnalysisAPI';
import * as venomPoolingAPI from "./venomPoolingAPI";
import { SPECIMEN_HEADERS, 
    VENOM_ANALYSIS_HEADERS, 
    EXTRACTION_HEADERS, 
    EXTRACTION_ENTRY_HEADERS, 
    MONTHLY_POOL_HEADERS,
    SPECIES_BATCH_POOL_HEADERS,
    SPECIES_BATCH_POOL_IN_VENOM_LOT_HEADERS,
    MONTHLY_POOL_DAILY_EXTRACTION_HEADERS,
    MONTHLY_POOL_EXTERNALLY_SUPPLIED_HEADERS,
    VENOM_LOT_HEADERS
} from "./exportHeaders";

const BLOB_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';


async function exportSpecimen(specimenID) {
    const specimens = (await specimenAPI.getSpecimen({ SpecimenID: specimenID })).Specimens;
    const venomAnalysis = await venomAnalysisAPI.getHistoricalAnalysis(specimenID, -1, -1, -1);
    const filename = `Specimen_${specimens[0].SpecimenDisplayID}_${moment().format("YYYYMMDD_HHmm")}.xlsx`;

    specimens[0].ArrivalDate = formatDateTimeToDate(specimens[0].ArrivalDate);

    const workbook = new ExcelJS.Workbook();
    
    addWorksheet(workbook, "Specimen", SPECIMEN_HEADERS, specimens);

    addVenomAnalysisData(workbook, venomAnalysis);

    // write to a buffer and trigger a "download" of the new file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: BLOB_TYPE });
    FileSaver.saveAs(blob, filename);
}

async function exportDailyExtraction(dailyExtractionSessionID) {
    const dailyExtractionSession = await venomPoolingAPI.getDailyExtractionSession({ dailyExtractionSessionID });
    const sessions = await venomPoolingAPI.getDailyExtractionSessionEntries({ DailyExtractionSessionID: dailyExtractionSessionID });

    // Put on the calculated fields
    sessions.forEach(s => {
        s.PrimaryExtractorUser = formatUserName(s.PrimaryExtractorEmail, s.PrimaryExtractorUserFirstName, s.PrimaryExtractorUserLastName);
        s.AssistantExtractorUser = formatUserName(s.AssistantEmail, s.AssistantUserFirstName, s.AssistantUserLastName);
        s.RecordedAt = formatDateTimeToLocal(s.RecordedAt);
    })

    const venomAnalysis = await venomAnalysisAPI.getHistoricalAnalysis(-1, dailyExtractionSessionID, -1, -1);

    const filename = `DailyExtractionSession_${dailyExtractionSessionID}_${moment().format("YYYYMMDD_HHmm")}.xlsx`;

    const workbook = new ExcelJS.Workbook();

    var extractionRow = [{
        Barcode: dailyExtractionSession.Barcode,
        SessionDate: dailyExtractionSession.SessionDate.substring(0, 10),
        RoomName: dailyExtractionSession.RoomName,
        RackNumber: dailyExtractionSession.RackNumber,
        SpeciesFullName: dailyExtractionSession.SpeciesFullName,
        Weight: dailyExtractionSession.Weight,
        Comment: dailyExtractionSession.Comment,
        SessionStartTime: formatDateTimeToLocal(dailyExtractionSession.SessionStartTime),
        SessionStartUser: formatUserName(dailyExtractionSession.SessionStartUserEmail, dailyExtractionSession.SessionStartUserFirstName, dailyExtractionSession.SessionStartUserLastName),
        SessionEndTime: formatDateTimeToLocal(dailyExtractionSession.SessionEndTime),
        SessionEndUser: formatUserName(dailyExtractionSession.SessionEndUserEmail, dailyExtractionSession.SessionEndUserFirstName, dailyExtractionSession.SessionEndUserLastName),
        VenomRefrigeratedTime: dailyExtractionSession.VenomRefrigeratedTime,
        VenomRefrigeratedUser: formatUserName(dailyExtractionSession.VenomRefrigeratedUser, dailyExtractionSession.VenomRefrigeratedUserFirstName, dailyExtractionSession.VenomRefrigeratedUserLastName)
    }];

    addWorksheet(workbook, "Daily Extraction Session", EXTRACTION_HEADERS, extractionRow);
    addWorksheet(workbook, "Daily Extraction Session Entries", EXTRACTION_ENTRY_HEADERS, sessions);

    addVenomAnalysisData(workbook, venomAnalysis);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: BLOB_TYPE });
    FileSaver.saveAs(blob, filename);
}

async function exportMonthlyPool(monthlyPoolID) {
    const pool = await venomPoolingAPI.getMonthlyPool({ monthlyPoolID: monthlyPoolID });
    const poolData = await venomPoolingAPI.getMonthlyPoolDetails({ monthlyPoolID: monthlyPoolID });

    const venomAnalysis = await venomAnalysisAPI.getHistoricalAnalysis(-1, -1, monthlyPoolID, -1);

    const filename = `MonthlyPool_${monthlyPoolID}_${moment().format("YYYYMMDD_HHmm")}.xlsx`;
    const workbook = new ExcelJS.Workbook();

    pool.TotalWeight = poolData.TotalWeight;
    pool.PoolCreatedByUser = formatUserName(pool.PoolCreatedByEmail, pool.PoolCreatedUserFirstName, pool.PoolCreatedUserLastName);
    pool.PoolClosedByUser = formatUserName(pool.PoolClosedByEmail, pool.PoolClosedUserFirstName, pool.PoolClosedUserLastName);
    pool.PoolClosedTime = formatDateTimeToLocal(pool.PoolClosedTime);
    pool.PoolCreatedTime = formatDateTimeToLocal(pool.PoolCreatedTime);
    pool.InSpeciesBatchPool = pool.SpeciesBatchPoolID && pool.SpeciesBatchPoolID > 0 ? "Yes" : "No";

    poolData.DailySessions.forEach(session => {
        session.SessionStartTime = formatDateTimeToLocal(session.SessionStartTime);
    });

    addWorksheet(workbook, "Monthly Pool", MONTHLY_POOL_HEADERS, [ pool ]);
    addWorksheet(workbook, "Extraction Sessions", MONTHLY_POOL_DAILY_EXTRACTION_HEADERS, poolData.DailySessions);
    addWorksheet(workbook, "Externally Supplied Venom", MONTHLY_POOL_EXTERNALLY_SUPPLIED_HEADERS, poolData.ExternalVenom);

    addVenomAnalysisData(workbook, venomAnalysis);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: BLOB_TYPE });
    FileSaver.saveAs(blob, filename);
}

async function exportSpeciesBatchPool(speciesBatchPoolID) {
    const sbp = await venomPoolingAPI.getSpeciesBatchPools({ speciesBatchPoolID });

    const venomAnalysis = await venomAnalysisAPI.getHistoricalAnalysis(-1, -1, -1, -1, speciesBatchPoolID);

    const filename = `SpeciesBatchPool_${speciesBatchPoolID}_${moment().format("YYYYMMDD_HHmm")}.xlsx`;
    const workbook = new ExcelJS.Workbook();

    sbp.CreatedByUser = formatUserName(sbp.CreatedByEmail, sbp.CreatedByFullName);
    sbp.ClosedByUser = formatUserName(sbp.ClosedByEmail, sbp.ClosedByFullName);

    sbp.CreatedTime = formatDateTimeToLocal(sbp.CreatedTime);
    sbp.ClosedTime = formatDateTimeToLocal(sbp.ClosedTime);

    sbp.MonthlyPools.forEach(pool => {
        pool.PoolCreatedByUser = formatUserName(pool.PoolCreatedByEmail, pool.PoolCreatedUserFirstName, pool.PoolCreatedUserLastName);
        pool.PoolClosedByUser = formatUserName(pool.PoolClosedByEmail, pool.PoolClosedUserFirstName, pool.PoolClosedUserLastName);
        pool.PoolClosedTime = formatDateTimeToLocal(pool.PoolClosedTime);
        pool.PoolCreatedTime = formatDateTimeToLocal(pool.PoolCreatedTime);
        pool.InSpeciesBatchPool = pool.SpeciesBatchPoolID && pool.SpeciesBatchPoolID > 0 ? "Yes" : "No";
    });

    addWorksheet(workbook, "Species Batch Pool", SPECIES_BATCH_POOL_HEADERS, [sbp]);

    addWorksheet(workbook, "Monthly Pools", MONTHLY_POOL_HEADERS, sbp.MonthlyPools);

    addVenomAnalysisData(workbook, venomAnalysis);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: BLOB_TYPE });
    FileSaver.saveAs(blob, filename);
}

async function exportVenomLot(venomLotID) {
    const venomLot = await venomPoolingAPI.getVenomLots({ venomLotID });

    const venomAnalysis = await venomAnalysisAPI.getHistoricalAnalysis(-1, -1, -1, venomLotID);

    const filename = `VenomLot_${venomLotID}_${moment().format("YYYYMMDD_HHmm")}.xlsx`;
    const workbook = new ExcelJS.Workbook();

    venomLot.CreatedByUser = formatUserName(venomLot.CreatedByEmail, venomLot.CreatedByFullName);
    venomLot.ClosedByUser = formatUserName(venomLot.ClosedByEmail, venomLot.ClosedByFullName);

    venomLot.CreatedTime = formatDateTimeToLocal(venomLot.CreatedTime);
    venomLot.ClosedTime = formatDateTimeToLocal(venomLot.ClosedTime);
    venomLot.VenomLotDate = formatDateTimeToLocal(venomLot.VenomLotDate);

    venomLot.SpeciesBatchPools.forEach(sbp =>{
        sbp.CreatedByUser = formatUserName(sbp.CreatedByEmail, sbp.CreatedByFullName);
        sbp.ClosedByUser = formatUserName(sbp.ClosedByEmail, sbp.ClosedByFullName);

        sbp.ClosedTime = formatDateTimeToLocal(sbp.ClosedTime);
        sbp.CreatedTime = formatDateTimeToLocal(sbp.ClosedTime);
    });

    addWorksheet(workbook, "Venom Lot", VENOM_LOT_HEADERS, [venomLot]);

    addWorksheet(workbook, "Species Batch Pools", SPECIES_BATCH_POOL_IN_VENOM_LOT_HEADERS, venomLot.SpeciesBatchPools);

    addVenomAnalysisData(workbook, venomAnalysis);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: BLOB_TYPE });
    FileSaver.saveAs(blob, filename);
}

export default {
    exportSpecimen,
    exportDailyExtraction,
    exportMonthlyPool,
    exportVenomLot,
    exportSpeciesBatchPool
}

function addVenomAnalysisData(workbook, venomAnalysis) {
    // Map the array to a better list
    var valuesFlattened = [];

    venomAnalysis.forEach(va => {
        valuesFlattened.push({
            AnalysisDate: va.AnalysisDate.substring(0, 10),
            Name: "",
            LowRange: "",
            HighRange: "",
            Comments: va.OtherComments,
            Attachment: va.S3Path == null ? "No" : "Yes"
        });

        va.Proteins.forEach(p => {
            valuesFlattened.push({
                AnalysisDate: "",
                Name: p.Name,
                LowRange: p.LowRange,
                HighRange: p.HighRange,
                Comments: p.Comments,
                Attachment: ""
            });
        })
    })
    

    addWorksheet(workbook, "Venom Analysis", VENOM_ANALYSIS_HEADERS, valuesFlattened);
}

function addWorksheet(workbook, name, headers, data) {
    const worksheet = workbook.addWorksheet(name);

    worksheet.columns = headers;

    for (let i = 0; i < data.length; i++) {
        const row = worksheet.getRow(i + 2); // there is no row 0, and row 1 is the header row
        row.values = data[i];
    }

    worksheet.getRow(1).font = {
        bold: true
    };
}

function formatUserName(email, firstname, surname) {
    if (!email) {
        return "";
    }

    var desc = "";
    if (firstname && firstname != "") {
        desc = firstname;
    }

    if (surname && surname != "") {
        desc += (desc == "" ? "" : " ") + surname;
    }

    desc += (desc == "" ? "" : " (") + email + (desc == "" ? "" : ")");

    return desc;
}

function formatDateTimeToLocal(time) {
    if (time == null || time == undefined) {
        return "";
    }

    return moment.utc(time).local().format();
}

function formatDateTimeToDate(time) {
    if (time == null || time == undefined) {
        return "";
    } else if (time.length <= 10) {
        return time;
    } else {
        return time.substring(0, 10)
    }
}